import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { statusMeta } from '../mock/mock';
import { TrendingUp, Users, Send, MailOpen, Play, ArrowRight, AlertCircle, Sparkles } from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState({ total_contacts:0, active_campaigns:0, emails_sent:0, open_rate:0, reply_rate:0, bounce_rate:0 });
  const [campaigns, setCampaigns] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get('/analytics/summary').then(r => setSummary(r.data)).catch(()=>{});
    api.get('/campaigns').then(r => setCampaigns(r.data.slice(0, 5))).catch(()=>{});
    api.get('/contacts').then(r => setRecent(r.data.slice(0, 6))).catch(()=>{});
  }, []);

  const stats = [
    { label: 'Total contacts', value: summary.total_contacts, icon: Users, trend: summary.total_contacts ? 'across your queues' : 'Import your first CV' },
    { label: 'Active campaigns', value: summary.active_campaigns, icon: Play, trend: summary.active_campaigns ? 'sending on schedule' : 'Create your first' },
    { label: 'Emails sent', value: summary.emails_sent, icon: Send, trend: 'lifetime' },
    { label: 'Open rate', value: summary.emails_sent ? summary.open_rate + '%' : '—', icon: MailOpen, trend: 'Industry avg 42%' },
  ];

  const proBadge = user?.plan === 'pro';

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl">Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.</h1>
          <p className="text-zinc-500 mt-1">Here’s where your outreach stands today.</p>
        </div>
        <div className="flex gap-2 items-center">
          {proBadge && <Badge className="bg-[#1a1815] text-[#f5d67a] border border-[#3a2f18]"><Sparkles size={12} className="mr-1"/>Pro</Badge>}
          <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900 hover:text-[#f5d67a]" asChild>
            <Link to="/app/upload">Import contacts</Link>
          </Button>
          <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" asChild>
            <Link to="/app/campaigns/new">New campaign</Link>
          </Button>
        </div>
      </div>

      {!user?.outlook_connected && (
        <Card className="mt-6 p-4 bg-[#1a1613] border-[#3a2f18] flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-[#0a0a0b] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a]"><AlertCircle size={18}/></div>
          <div className="flex-1">
            <div className="text-sm text-zinc-100">Connect your Outlook to start sending</div>
            <div className="text-xs text-zinc-500">Emails will be sent from your address; replies land in your inbox.</div>
          </div>
          <Button size="sm" className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black" asChild><Link to="/app/settings">Connect</Link></Button>
        </Card>
      )}

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <Card key={s.label} className="bg-[#111110] border-zinc-900 p-5">
            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-500">{s.label}</div>
              <s.icon size={16} className="text-[#d4a94a]"/>
            </div>
            <div className="mt-3 font-display text-3xl">{s.value}</div>
            <div className="mt-2 flex items-center gap-1 text-xs text-zinc-500"><TrendingUp size={12}/>{s.trend}</div>
          </Card>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-[#111110] border-zinc-900">
          <div className="p-5 border-b border-zinc-900 flex items-center justify-between">
            <div>
              <div className="font-display text-lg">Your campaigns</div>
              <div className="text-xs text-zinc-500">Recent sending activity</div>
            </div>
            <Link to="/app/campaigns" className="text-sm text-[#f5d67a] hover:underline flex items-center gap-1">View all <ArrowRight size={14}/></Link>
          </div>
          <div className="divide-y divide-zinc-900">
            {campaigns.length===0 && <div className="p-8 text-center text-zinc-500 text-sm">No campaigns yet. <Link to="/app/campaigns/new" className="text-[#f5d67a] hover:underline">Create one</Link></div>}
            {campaigns.map(c => {
              const m = c.metrics || {};
              return (
                <Link key={c.id} to={`/app/campaigns/${c.id}`} className="flex items-center justify-between p-5 hover:bg-zinc-900/40 transition-colors">
                  <div>
                    <div className="text-zinc-100">{c.name}</div>
                    <div className="text-xs text-zinc-500 mt-1">{c.contact_count} contacts · {c.steps?.length || 0} steps</div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="hidden md:grid grid-cols-3 gap-6 text-right">
                      <div><div className="text-xs text-zinc-500">Sent</div><div className="text-sm text-zinc-200">{m.sent||0}</div></div>
                      <div><div className="text-xs text-zinc-500">Opens</div><div className="text-sm text-zinc-200">{m.opens||0}</div></div>
                      <div><div className="text-xs text-zinc-500">Replies</div><div className="text-sm text-[#f5d67a]">{m.replies||0}</div></div>
                    </div>
                    <Badge className={c.status==='active' ? 'bg-emerald-950 text-emerald-300 border border-emerald-900' : 'bg-zinc-900 text-zinc-400 border border-zinc-800'}>{c.status}</Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>

        <Card className="bg-[#111110] border-zinc-900">
          <div className="p-5 border-b border-zinc-900 flex items-center justify-between">
            <div>
              <div className="font-display text-lg">Latest activity</div>
              <div className="text-xs text-zinc-500">Recent contact status</div>
            </div>
            <Link to="/app/contacts" className="text-sm text-[#f5d67a] hover:underline">All</Link>
          </div>
          <div className="divide-y divide-zinc-900">
            {recent.length===0 && <div className="p-8 text-center text-zinc-500 text-sm">No contacts yet. <Link to="/app/upload" className="text-[#f5d67a] hover:underline">Import a CV</Link></div>}
            {recent.map(c => {
              const m = statusMeta[c.status] || statusMeta.queued;
              return (
                <div key={c.id} className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{c.company}{c.job_title ? ' · ' + c.job_title : ''}</div>
                  </div>
                  <Badge className={`${m.color} text-[10px]`}>{m.label}</Badge>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
